import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuthContext, useVisitContext } from '@hooks';
import { HeaderLayout } from '@layouts';
import ClosedPage from '@components/ClosedPage';
import PrimaryButton from '@components/PrimaryButton';
import LoadingSpinner from '@components/LoadingSpinner';
import { ROUTES, purposesLoadingSpinner } from '@constants';
import table from '@assets/table.png';

import SignIn from '../SignIn';
import VerificationCodeScreen from '../VerificationCodeScreen/VerificationCodeScreen';
import { useStyles } from './styles';

export default function VisitScreen() {
  const { t } = useTranslation(['auth', 'visitScreen', 'button']);
  const history = useHistory();
  const classes = useStyles();
  const { placeId, tableId } = useParams();

  const [open, setOpen] = useState(false);
  const [nextScreen, setNextScreen] = useState(ROUTES.SLIDER_SCREEN);
  const [isVerificationScreen, updateIsVerificationScreen] = useState(false);
  const [email, setEmail] = useState('');
  const [pageMayClosed, setPageMayClosed] = useState(false);
  const { createVisit } = useVisitContext();

  const { session } = useAuthContext();

  const {
    getDataForVisitPage,
    setPlaceData: setPlaceDataContext,
    dataForPage,
  } = useVisitContext();

  useEffect(() => {
    setPlaceDataContext({ placeId, tableId });
    const isDataForPageExist = Object.values(dataForPage).every(
      item => Object.values(item).length,
    );

    const fetchDataForPage = async () => {
      const dataForPageSuccessfullyGetted = await getDataForVisitPage({
        placeId,
        tableId,
      });
      await createVisit({ placeId, tableId });

      if (!dataForPageSuccessfullyGetted) {
        history.push('/error-visit-screen');
      }
    };

    if (!isDataForPageExist) fetchDataForPage();
    // eslint-disable-next-line
  }, []);

  const handleClose = async () => {
    setOpen(false);
    if (nextScreen === ROUTES.MENU_SCREEN) {
      await createVisit({
        placeId,
        tableId,
      });

      history.push(nextScreen, {
        from: ROUTES.VISIT_SCREEN,
        data: {
          nextScreen,
        },
      });
    }
  };

  const CloseVerificationScreen = () => {
    updateIsVerificationScreen(false);

    if (nextScreen === ROUTES.MENU_SCREEN) {
      history.push(nextScreen, {
        from: ROUTES.VISIT_SCREEN,
        data: {
          nextScreen,
        },
      });
    }
  };

  const handleNextPage = async nextScreen => {
    const screen = sessionStorage.getItem('showVisit');

    if (
      (!session?.authenticated && screen !== 'visit') ||
      (!session?.authenticated && nextScreen === ROUTES.SLIDER_SCREEN)
    ) {
      setNextScreen(nextScreen);
      setOpen(true);
    } else {
      history.push(nextScreen, {
        from: ROUTES.VISIT_SCREEN,
        data: {
          nextScreen,
        },
      });
    }
  };

  const OpenVerificationScreen = () => {
    updateIsVerificationScreen(true);
    setTimeout(() => {
      setOpen(false);
    }, 800);
  };

  const isDataForPageLoading = !(
    dataForPage.place.imageUrl &&
    dataForPage.place.name &&
    dataForPage.table.name
  );

  const handlePossibilityClosingPage = () => {
    const stringifySession = localStorage.getItem('session');
    const parsedSession = JSON.parse(stringifySession);
    const sessionExist = parsedSession
      ? !!Object.keys(parsedSession).length
      : false;

    if (isVerificationScreen && sessionExist) setPageMayClosed(true);
  };

  useEffect(() => {
    window.addEventListener('storage', handlePossibilityClosingPage);
    return () => {
      window.removeEventListener('storage', handlePossibilityClosingPage);
    };
    // eslint-disable-next-line
  }, [isVerificationScreen]);

  if (isDataForPageLoading) {
    return <LoadingSpinner purpose={purposesLoadingSpinner.regular} />;
  }

  return (
    <HeaderLayout pageMayClosed={pageMayClosed}>
      {pageMayClosed ? (
        <ClosedPage />
      ) : (
        <div className={classes.startScreen}>
          <div className={classes.startScreenContent}>
            <div>
              <img
                className={classes.startScreenImg}
                src={dataForPage.place.imageUrl}
                alt="logo"
              />
              <p className={clsx(classes.titleCommon, classes.title)}>
                Welcome to {dataForPage.place.name}
              </p>
              <p className={clsx(classes.titleCommon, classes.subTitle)}>
                You are seated at
              </p>
            </div>
            <div className={classes.tableContainer}>
              <img src={table} alt="table" />
              <p className={classes.tableLettering}>{t('table')}</p>
              <p className={classes.tableNumber}>{dataForPage.table.name}</p>
            </div>
            <div className={classes.buttonGroup}>
              <PrimaryButton
                text={t('button:cocktailMenu')}
                handleClick={() => handleNextPage(ROUTES.MENU_SCREEN)}
              />
            </div>
            <SignIn
              open={open}
              screen={'visit'}
              handleClose={handleClose}
              OpenVerificationScreen={OpenVerificationScreen}
              root={nextScreen}
              email={email}
              title={t('signIn.title')}
              subTitle={t('signIn.subTitle')}
              setEmail={setEmail}
            />
            <VerificationCodeScreen
              isVerificationScreen={isVerificationScreen}
              handleClose={CloseVerificationScreen}
              email={email}
              nextScreen={nextScreen}
              root={nextScreen}
              setEmail={setEmail}
            />
          </div>
        </div>
      )}
    </HeaderLayout>
  );
}
